import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, Loader2, MapPin, Clock, Mail, Instagram } from "lucide-react";
import { MaskLines, Reveal } from "@/components/Reveal";
import { INFO } from "@/data/site";
import { api } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border-b border-line py-3 font-sans text-lg text-ink placeholder:text-muted/50 focus:border-harissa focus:outline-none transition-colors";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Bitte Name, E-Mail und Nachricht ausfüllen.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Nachricht gesendet!");
    } catch {
      toast.error("Etwas ist schiefgelaufen. Versuch es nochmal.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-28 sm:pt-36 min-h-screen">
      <header className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-harissa mb-5">Kontakt</p>
        <h1 className="font-display uppercase text-[16vw] sm:text-[13vw] leading-[0.82] tracking-tight">
          <MaskLines lines={["Sag", <span key="s" className="text-stroke-harissa">Hallo.</span>]} />
        </h1>
      </header>

      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Info */}
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-sans text-xl text-muted max-w-md">
                Fragen, Kooperationen, Privat-Events oder einfach Hunger auf ein Gespräch? Schreib uns.
              </p>
            </Reveal>
            <div className="mt-12 space-y-px bg-line border-y border-line">
              {[
                { icon: MapPin, label: "Standort", val: INFO.address },
                { icon: Clock, label: "Öffnungszeiten", val: INFO.hours },
                { icon: Mail, label: "E-Mail", val: INFO.email },
                { icon: Instagram, label: "Instagram", val: INFO.instagram, href: INFO.instagramUrl },
              ].map((r, i) => (
                <Reveal key={i} delay={i * 0.05} className="bg-base">
                  <div className="flex items-center gap-4 py-5">
                    <r.icon size={18} className="text-harissa shrink-0" />
                    <div>
                      <p className="font-mono text-[11px] uppercase tracking-widest text-muted">{r.label}</p>
                      {r.href ? (
                        <a href={r.href} target="_blank" rel="noreferrer" className="font-head text-xl hover:text-mango transition-colors" data-testid={`contact-info-${i}`}>{r.val}</a>
                      ) : (
                        <p className="font-head text-xl" data-testid={`contact-info-${i}`}>{r.val}</p>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-6 lg:col-start-7">
            {done ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-plantain p-10 text-center" data-testid="contact-success">
                <div className="w-16 h-16 border border-plantain mx-auto flex items-center justify-center mb-6">
                  <Check className="text-plantain" size={32} />
                </div>
                <h2 className="font-head font-[800] text-3xl uppercase">Merci!</h2>
                <p className="font-sans text-muted mt-4">Deine Nachricht ist angekommen. Wir melden uns bald.</p>
                <button onClick={() => { setDone(false); setForm({ name: "", email: "", subject: "", message: "" }); }} className="font-mono text-xs uppercase tracking-widest mt-8 border border-line px-6 py-3 hover:border-harissa hover:text-harissa transition-colors" data-testid="contact-again-btn">
                  Neue Nachricht
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                <label className="block">
                  <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted">Name *</span>
                  <input className={inputCls + " mt-2"} value={form.name} onChange={set("name")} placeholder="Dein Name" data-testid="contact-input-name" />
                </label>
                <label className="block">
                  <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted">E-Mail *</span>
                  <input type="email" className={inputCls + " mt-2"} value={form.email} onChange={set("email")} placeholder="du@mail.ch" data-testid="contact-input-email" />
                </label>
                <label className="block">
                  <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted">Betreff</span>
                  <input className={inputCls + " mt-2"} value={form.subject} onChange={set("subject")} placeholder="Worum geht's?" data-testid="contact-input-subject" />
                </label>
                <label className="block">
                  <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted">Nachricht *</span>
                  <textarea rows="5" className={inputCls + " mt-2 resize-none"} value={form.message} onChange={set("message")} placeholder="Schreib uns…" data-testid="contact-input-message" />
                </label>
                <button type="submit" disabled={loading} className="inline-flex items-center gap-2 bg-harissa text-base font-mono text-sm uppercase tracking-widest px-10 py-4 hover:bg-ink transition-colors disabled:opacity-60" data-testid="contact-submit-btn">
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Sende…</> : "Nachricht senden"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
